import { computed, signal } from '@angular/core';
import {
  PaymentMethod,
  Stripe,
  StripeCardCvcElement,
  StripeCardElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElementChangeEvent,
  StripeElements,
  StripeError,
  StripePaymentElement,
} from '@stripe/stripe-js';
import { defer, Observable, Subject, from, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retry } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';
import {
  borderRadius04,
  colorActionActive,
  colorMessageErrorBase,
  colorNeutral15,
  colorNeutralDefault,
  fontFamilyFallback,
  fontSize04,
  fontWeightMedium
} from '@sondermind/iris-design-tokens/design-tokens';
import {
  CreditCardForm,
  CreditCardFormKeys,
} from '@sondermind/utilities/models-credit-card';
import { BankAccountFormKeys } from '@sondermind/utilities/models-bank-account';
import { ServiceError } from '@sondermind/utilities/models-http';

import { StripeLoader } from './stripe-loader';

export interface IBannerMessage {
  title: string;
}

type StripeFormElement =
  | StripeCardNumberElement
  | StripeCardExpiryElement
  | StripeCardCvcElement;

interface IStripeFormElementSpec {
  type: 'cardNumber' | 'cardExpiry' | 'cardCvc';
  selector: string;
}

const CARD_ELEMENT_SPECS: readonly IStripeFormElementSpec[] = [
  { type: 'cardNumber', selector: '#stripe-card-number-element' },
  { type: 'cardExpiry', selector: '#stripe-card-exp-element' },
  { type: 'cardCvc', selector: '#stripe-card-cvc-element' },
];

/**
 * Generic payment method result supporting cards, bank accounts, and wallets.
 * Used by the new unified createPaymentMethod() API.
 *
 * This is a discriminated union based on `type`:
 * - type === 'card' → cardDetails is required
 * - type === 'us_bank_account' → bankDetails is required
 * - other types (wallets) → no additional details
 */
export type IPaymentMethodResult =
  | {
    paymentMethodId: string;
    type: 'card';
    cardDetails: {
      brand: string;
      last4: string;
      country: string;
      cardId?: string;
    };
  }
  | {
    paymentMethodId: string;
    type: 'us_bank_account';
    bankDetails: {
      bankName: string;
      last4: string;
      accountHolderType?: string;
    };
  }
  | {
    paymentMethodId: string;
    type: 'apple_pay' | 'google_pay' | 'link' | string;
  };

const GENERIC_CARD_ERROR = "Something didn't work, please try again.";
const VALIDATION_ERROR_FALLBACK = 'Please check your payment details';
const INVALID_REQUEST_FALLBACK = 'Unable to process payment — please try again';

/** Stripe asks merchants to show these to the cardholder as a generic decline, never the reason. */
const HIDDEN_DECLINE_CODES: ReadonlySet<string> = new Set([
  'fraudulent',
  'lost_card',
  'stolen_card',
  'merchant_blacklist'
]);

const STRIPE_LOAD_FAILURE_MESSAGE =
  "We're having trouble loading our payment provider. " +
  'Please refresh the page or try again later.';

export class StripePaymentMethodBase {
  /** True once Stripe.js has loaded and `stripe`/`elements` are usable. */
  readonly loaded = signal(false);

  /** Set to the load error if Stripe.js failed to initialize, otherwise null. */
  readonly loadError = signal<unknown | null>(null);

  /** Latest aggregated card-error message, or null when there are no errors. */
  readonly cardErrorMessage = signal<IBannerMessage | null>(null);

  /** Per-element error messages, keyed by element index. */
  readonly stripeErrors = signal<Record<number, string | null>>({});

  /** Per-element completeness flags, keyed by element index. */
  readonly stripeComplete = signal<Record<number, boolean>>({});

  /** Inverse of `loaded`, useful for `[disabled]` bindings. */
  readonly stripeNotLoaded = computed(() => !this.loaded());

  /** True when every mounted Stripe element reports complete. */
  readonly stripeFormComplete = computed(() => {
    const complete = this.stripeComplete();
    const entries = Object.values(complete);
    if (entries.length === 0) {
      return false;
    }
    return entries.every(Boolean);
  });

  /**
   * Emits the card-number element when the form is submitted with valid
   * input. Consumers pass this element to `createCardPaymentMethod()`.
   */
  readonly stripeElementSubject$ = new Subject<StripeCardNumberElement>();

  /** Stripe SDK references — only safe to read after `loaded()` is true. */
  stripe: Stripe | null = null;
  elements: StripeElements | null = null;

  /** The form element wired up by `registerStripeElements`. */
  stripeForm: HTMLFormElement | null = null;

  /** The Stripe elements mounted by `mountStripeElements`. */
  stripeFormElements: StripeFormElement[] = [];

  /** Handler references for cleanup. */
  private formSubmitHandler: ((event: Event) => void) | null = null;
  private elementChangeHandlers: Array<{
    element: StripeFormElement;
    handler: (event: StripeElementChangeEvent) => void;
  }> = [];

  /**
   * @param loader - resolves Stripe.js. Pass `inject(STRIPE_LOADER)`; specs
   *   override that token so no unit test reaches js.stripe.com. Required
   *   rather than defaulted so a new call site cannot silently reintroduce
   *   the network load into a spec.
   */
  constructor(
    stripeKey: string,
    private logger: Logger,
    loader: StripeLoader,
  ) {
    loader(stripeKey).then(
      (stripe) => {
        if (!stripe) {
          const err = new Error('Stripe.js returned null — script may be blocked.');
          this.handleLoadFailure(err);
          return;
        }
        this.stripe = stripe;
        this.elements = stripe.elements({
          appearance: this.createAppearanceConfig(),
        });
        this.loaded.set(true);
      },
      (err: unknown) => this.handleLoadFailure(err),
    );
  }

  /** Handles Stripe.js load failure by setting error signals and logging. */
  protected handleLoadFailure(err: unknown): void {
    this.loadError.set(err);
    this.cardErrorMessage.set({
      title: STRIPE_LOAD_FAILURE_MESSAGE,
    });
    this.logger.error(`Error loading Stripe: ${String(err)}`);
  }

  /**
   * Mounts separate card input fields (card number, expiry, CVC).
   * Now uses modern Appearance API for styling.
   *
   * Creates 3 individual Stripe Elements that you mount into separate DOM containers:
   * - #stripe-card-number-element
   * - #stripe-card-exp-element
   * - #stripe-card-cvc-element
   *
   * **For new forms:** Consider using `mountPaymentElement()` instead, which provides
   * a single unified field supporting cards, bank accounts, and digital wallets.
   *
   * @returns Array of mounted Stripe Elements (StripeCardNumberElement, etc.)
   */
  mountStripeElements(): StripeFormElement[] {
    if (!this.elements) {
      throw new Error('mountStripeElements called before Stripe finished loading.');
    }

    const specs = CARD_ELEMENT_SPECS;

    // Seed `stripeComplete` with one false entry per element BEFORE mounting
    // so the `stripeFormComplete` computed knows how many entries to expect
    // even if `.mount()` synchronously fires a `change` event during the loop
    // (e.g. on a remount where Stripe returns a cached element with prior
    // state). Without this pre-seed, an early sync change event could leave
    // `stripeComplete` as `{ <index>: true }` with `entries.every(Boolean)`
    // returning true off a single complete element.
    const seed: Record<number, boolean> = {};
    for (let i = 0; i < specs.length; i += 1) {
      seed[i] = false;
    }
    this.stripeComplete.set(seed);

    const mounted = specs.map((spec) => {
      // The Stripe Elements `getElement`/`create` overloads don't type-narrow on a string variable,
      // so we route through the typed helper. Each branch returns its concrete subtype.
      const element = this.getOrCreateElement(spec.type);
      element.mount(spec.selector);
      return element;
    });

    this.stripeFormElements = mounted;
    return mounted;
  }

  /**
   * Wires Stripe element change events into the `stripeErrors`,
   * `stripeComplete`, and `cardErrorMessage` signals, and emits
   * `stripeElementSubject$` on form submit when every element is complete.
   *
   * @param elements - the elements returned by `mountStripeElements`
   * @param form - the form element wrapping the Stripe element placeholders.
   *   Pass an `ElementRef.nativeElement` from `viewChild()` rather than a
   *   selector string so we don't have to query the DOM ourselves.
   */
  registerStripeElements(elements: StripeFormElement[], form: HTMLFormElement): void {
    this.stripeForm = form;
    // NOTE: do NOT reset `stripeComplete` here. `mountStripeElements` seeds it
    // with one `false` entry per mounted element so `stripeFormComplete()`
    // can derive its expected count reactively. Clearing it would force the
    // computed back to `false` for the wrong reason (zero entries).
    this.stripeErrors.set({});

    // Clean up any existing handlers before registering new ones
    this.cleanupEventListeners();

    elements.forEach((element, index) => {
      // Each element subtype has its own narrow change-event signature, but
      // all of them carry the `error` and `complete` fields we read. Route
      // through `unknown` so the union's incompatible signatures don't
      // block this single, generic handler.
      const onChange = (event: StripeElementChangeEvent): void => this.onElementChange(event, index);
      (element as unknown as StripeCardElement).on('change', onChange);
      this.elementChangeHandlers.push({ element, handler: onChange });
    });

    const submitHandler = (event: Event): void => {
      event.preventDefault();

      if (!form.checkValidity() || !this.stripeFormComplete()) {
        return;
      }

      // CARD_ELEMENT_SPECS guarantees elements[0] is always cardNumber.
      this.stripeElementSubject$.next(elements[0] as unknown as StripeCardNumberElement);
    };

    form.addEventListener('submit', submitHandler);
    this.formSubmitHandler = submitHandler;
  }

  /**
   * Removes all registered event listeners. Call this before remounting elements
   * or when the component unmounts to prevent listener accumulation.
   */
  cleanupEventListeners(): void {
    // Remove form submit handler
    if (this.stripeForm && this.formSubmitHandler) {
      this.stripeForm.removeEventListener('submit', this.formSubmitHandler);
      this.formSubmitHandler = null;
    }

    // Remove Stripe element change handlers
    this.elementChangeHandlers.forEach(({ element, handler }) => {
      (element as unknown as StripeCardElement).off('change', handler);
    });
    this.elementChangeHandlers = [];
  }

  /**
   * Returns true when the surrounding form is valid AND every Stripe element
   * is complete. Use the `stripeFormComplete` signal directly when binding
   * to a template — this method is a convenience for imperative call sites.
   */
  getStripeFormValidity(): boolean {
    return Boolean(this.stripeForm?.checkValidity()) && this.stripeFormComplete();
  }

  /**
   * Maps a Stripe error response into a `ServiceError` shape consumable by
   * the form layer. Returns string-array values uniformly (i.e. `base` is
   * always `string[]`, never a bare string).
   */
  parseCardError(error: StripeError): ServiceError {
    // Non-card errors aren't really for a client to see.
    if (error.type !== 'card_error') {
      return { base: [GENERIC_CARD_ERROR] };
    }

    const { param } = error;
    if (param && Object.hasOwn(CreditCardFormKeys, param)) {
      let message = error.message ?? '';
      if (error.code?.startsWith('invalid_')) {
        message = 'Invalid';
      } else if (error.code?.startsWith('incorrect_')) {
        message = 'Incorrect';
      }
      return { [param]: [message] };
    }

    // Generic card error with no recognized field — surface the message.
    return { base: [this.formatStripeErrorMessage(error, GENERIC_CARD_ERROR)] };
  }

  /**
   * Creates a Stripe PaymentMethod supporting cards, bank accounts, and digital wallets.
   * Returns payment method ID with type-discriminated details (brand, last4, etc.).
   *
   * @param element - StripePaymentElement (new) or StripeCardNumberElement (legacy)
   * @param billingDetails - Optional billing information
   * @returns Observable<IPaymentMethodResult> with payment type and relevant details
   */
  createPaymentMethod(
    element: StripeCardNumberElement | StripePaymentElement,
    billingDetails?: CreditCardForm,
  ): Observable<IPaymentMethodResult> {
    if (!this.stripe) {
      return throwError(() => ({
        base: ['Stripe is not ready yet — please try again in a moment.'],
      } as ServiceError));
    }

    const isPaymentElement = this.isPaymentElement(element);

    // Use defer to re-create the promise on each subscription (including retries)
    return defer(() => {
      const request = isPaymentElement
        ? this.createWithPaymentElement(billingDetails)
        : this.createWithCardElement(element, billingDetails);
      return from(request);
    }).pipe(
      mergeMap((stripeResp) => {
        if (stripeResp.error) {
          return throwError(() => this.parsePaymentError(stripeResp.error));
        }

        const { paymentMethod } = stripeResp;
        if (!paymentMethod) {
          // Stripe returned neither error nor paymentMethod - invalid API response
          this.logger.error('Stripe returned invalid response: no error and no paymentMethod', {
            stripeResponse: stripeResp,
          });
          return throwError(() => ({
            base: ['No payment method returned from Stripe'],
          } as ServiceError));
        }

        return [this.mapPaymentMethod(paymentMethod)];
      }),
      retry({
        count: 1,
        delay: (err: unknown, retryCount: number) => {
          const isRetryable = this.isRetryableNetworkError(err);
          const shouldRetry = retryCount === 1 && isRetryable;

          if (!shouldRetry) {
            return throwError(() => err);
          }

          this.logger.warn('Retrying Stripe createPaymentMethod after network error', {
            error: err,
          });
          return timer(0); // Immediate retry
        },
      }),
      catchError((err: unknown) => {
        // Already a ServiceError? Pass through
        if (this.isServiceError(err)) {
          return throwError(() => err);
        }

        // Raw promise rejection (network timeout, CSP block, etc.) - log and normalize
        this.logger.error('Stripe createPaymentMethod promise rejected', { error: err });
        return throwError(() => ({
          base: ['Unable to connect to payment provider — please try again'],
        } as ServiceError));
      }),
    );
  }

  /**
   * Confirms a payment method setup for future use without charging (SetupIntent flow).
   * Use for digital wallets to properly signal "save for later" authorization.
   *
   * @param element - StripePaymentElement (new) or StripeCardNumberElement (legacy)
   * @param clientSecret - SetupIntent client_secret from your backend
   * @param returnUrl - URL to redirect back to after 3D Secure (defaults to current URL)
   * @returns Observable<IPaymentMethodResult> with validated payment method
   */
  confirmPaymentMethodSetup(
    element: StripeCardNumberElement | StripePaymentElement,
    clientSecret: string,
    returnUrl?: string,
  ): Observable<IPaymentMethodResult> {
    if (!this.stripe) {
      return throwError(() => ({
        base: ['Stripe is not ready yet — please try again in a moment.'],
      } as ServiceError));
    }

    if (!clientSecret?.trim()) {
      this.logger.error('confirmPaymentMethodSetup called without client_secret');
      return throwError(() => ({
        base: ['Setup configuration missing — please try again'],
      } as ServiceError));
    }

    const isPaymentElement = this.isPaymentElement(element);

    // Use defer to re-create the promise on each subscription (including retries)
    return defer(() => {
      // Payment Element uses stripe.confirmSetup with elements
      // Card Element uses stripe.confirmCardSetup with the card element directly
      const request = isPaymentElement
        ? this.stripe!.confirmSetup({
          elements: this.elements ?? undefined,
          clientSecret,
          confirmParams: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            return_url: returnUrl ?? window.location.href,
          },
        })
        : this.stripe!.confirmCardSetup(clientSecret, {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          payment_method: { card: element },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          return_url: returnUrl ?? window.location.href,
        });
      return from(request);
    }).pipe(
      mergeMap((result) => {
        if (result.error) {
          return throwError(() => this.parsePaymentError(result.error));
        }

        // Type narrowing: after checking error, result must have setupIntent
        if (!('setupIntent' in result) || !result.setupIntent) {
          this.logger.error('Stripe confirmSetup returned no setupIntent', { result });
          return throwError(() => ({
            base: ['Setup confirmation failed — please try again'],
          } as ServiceError));
        }

        const { setupIntent } = result;

        // SetupIntent must be 'succeeded' to be usable
        if (setupIntent.status !== 'succeeded') {
          this.logger.warn('SetupIntent not in succeeded state', {
            setupIntentStatus: setupIntent.status,
            setupIntentId: setupIntent.id,
          });
          return throwError(() => ({
            base: [`Payment method setup ${setupIntent.status} — please try again`],
          } as ServiceError));
        }

        // Extract PaymentMethod from SetupIntent
        // When backend creates SetupIntent with expand: ['payment_method'],
        // this will be the full PaymentMethod object with type/details.
        // Otherwise it's just the ID string.
        const paymentMethod = setupIntent.payment_method;

        if (!paymentMethod) {
          this.logger.error('SetupIntent succeeded but has no payment_method', { setupIntent });
          return throwError(() => ({
            base: ['No payment method returned after setup'],
          } as ServiceError));
        }

        // If it's already a PaymentMethod object, map it
        if (typeof paymentMethod !== 'string') {
          return [this.mapPaymentMethod(paymentMethod)];
        }

        // If it's just an ID string, return minimal result
        // Backend will enrich with details when storing
        return [{
          paymentMethodId: paymentMethod,
          type: 'card', // Default assumption - backend will correct if needed
        } as IPaymentMethodResult];
      }),
      retry({
        count: 1,
        delay: (err: unknown, retryCount: number) => {
          const isRetryable = this.isRetryableNetworkError(err);
          const shouldRetry = retryCount === 1 && isRetryable;

          if (!shouldRetry) {
            return throwError(() => err);
          }

          this.logger.warn('Retrying Stripe confirmSetup after network error', {
            error: err,
          });
          return timer(0);
        },
      }),
      catchError((err: unknown) => {
        if (this.isServiceError(err)) {
          return throwError(() => err);
        }
        this.logger.error('Stripe confirmSetup promise rejected', { error: err });
        return throwError(() => ({
          base: ['Unable to connect to payment provider — please try again'],
        } as ServiceError));
      }),
    );
  }

  /**
   * Mounts a unified Payment Element supporting cards, bank accounts, and digital wallets.
   * Requires: `<div id="payment-element"></div>` in your template.
   *
   * @param selector - CSS selector for the container (e.g., '#payment-element')
   * @returns The mounted StripePaymentElement
   */
  mountPaymentElement(selector: string): StripePaymentElement {
    if (!this.elements) {
      throw new Error('mountPaymentElement called before Stripe finished loading.');
    }

    const paymentElement = this.elements.create('payment', {
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
      },
    });

    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Payment Element container not found: ${selector}`);
    }

    paymentElement.mount(selector);
    return paymentElement;
  }

  /**
   * Creates a bank account token for Stripe Connect external accounts (provider payouts only).
   * Uses the Token API which is still recommended for Connect external accounts
   * because SetupIntent with bank accounts requires micro-deposit verification.
   *
   * NOTE: This is for provider payout accounts only, not for client payment methods.
   * Client payments should use the modern PaymentIntent/SetupIntent APIs.
   *
   * @param form - Bank account details (snake_case to match Stripe API)
   * @returns Observable with token and bank account details
   */
  /* eslint-disable @typescript-eslint/naming-convention */
  createBankToken(
    form: {
      account_holder_name: string;
      account_holder_type: string;
      routing_number: string;
      account_number: string;
      currency: string;
      country: string;
    }
  ): Observable<{
      token: string;
      bankAccountId: string;
      bank_name: string;
      account_holder_name: string;
      last4: string;
    }> {
    /* eslint-enable @typescript-eslint/naming-convention */
    if (!this.stripe) {
      return throwError(() => ({
        base: ['Stripe is not ready yet — please try again in a moment.'],
      } as ServiceError));
    }

    const { stripe } = this;

    return defer(() => from(
      stripe.createToken('bank_account', {
        ...form,
        currency: form.currency || 'usd',
        country: form.country || 'us'
      })
    )).pipe(
      mergeMap((response) => {
        if (response.error) {
          return throwError(() => this.parseBankAccountError(response.error));
        }

        if (!response.token?.bank_account) {
          this.logger.error('Stripe createToken returned no bank_account', {
            response
          });
          return throwError(() => ({
            base: ['Failed to tokenize bank account'],
          } as ServiceError));
        }

        /* eslint-disable @typescript-eslint/naming-convention */
        const account = {
          token: response.token.id,
          bankAccountId: response.token.bank_account.id,
          bank_name: response.token.bank_account.bank_name || '',
          account_holder_name: response.token.bank_account.account_holder_name || '',
          last4: response.token.bank_account.last4 || ''
        };
        /* eslint-enable @typescript-eslint/naming-convention */

        return [account];
      }),
      catchError((err: unknown) => {
        if (this.isServiceError(err)) {
          return throwError(() => err);
        }
        this.logger.error('Stripe createBankToken failed', { error: err });
        return throwError(() => ({
          base: ['Unable to process bank account — please try again'],
        } as ServiceError));
      })
    );
  }

  /**
   * Creates a card token for Stripe Connect external accounts (provider payout debit cards only).
   * Uses the Token API which is still fully supported for Connect external accounts.
   *
   * NOTE: This is for provider payout accounts only, not for client payment methods.
   * Client payments should use the modern PaymentIntent/SetupIntent APIs.
   *
   * @param cardElement - Stripe card number element
   * @param cardOwnerData - Optional cardholder name and address data
   * @returns Observable with token and card details
   */
  createCardToken(
    cardElement: StripeCardNumberElement,
    cardOwnerData?: { name?: string; address_line1?: string; address_line2?: string; address_city?: string; address_state?: string; address_zip?: string }
  ): Observable<{
      token: string;
      cardId: string;
      brand: string;
      last4: string;
    }> {
    if (!this.stripe) {
      return throwError(() => ({
        base: ['Stripe is not ready yet — please try again in a moment.'],
      } as ServiceError));
    }

    const { stripe } = this;

    return defer(() => from(
      stripe.createToken(cardElement, { currency: 'usd', ...cardOwnerData })
    )).pipe(
      mergeMap((response) => {
        if (response.error) {
          return throwError(() => this.parseCardError(response.error));
        }

        if (!response.token?.card) {
          this.logger.error('Stripe createToken returned no card', {
            response
          });
          return throwError(() => ({
            base: ['Failed to tokenize card'],
          } as ServiceError));
        }

        const card = {
          token: response.token.id,
          cardId: response.token.card.id,
          brand: response.token.card.brand || '',
          last4: response.token.card.last4 || ''
        };

        return [card];
      }),
      catchError((err: unknown) => {
        if (this.isServiceError(err)) {
          return throwError(() => err);
        }
        this.logger.error('Stripe createCardToken failed', { error: err });
        return throwError(() => ({
          base: ['Unable to process card — please try again'],
        } as ServiceError));
      })
    );
  }

  /**
   * Parse bank account token errors into ServiceError format
   */
  private parseBankAccountError(error: StripeError): ServiceError {
    // Check for field-specific errors
    if (error.param && BankAccountFormKeys[error.param as keyof typeof BankAccountFormKeys]) {
      let message = error.message || 'Invalid';
      if (error.code?.startsWith('invalid_')) {
        message = 'Invalid';
      } else if (error.code?.startsWith('incorrect_')) {
        message = 'Incorrect';
      }

      return { [error.param]: [message] } as ServiceError;
    }

    // Generic error
    return {
      base: [error.message || 'Bank account validation failed'],
    } as ServiceError;
  }

  /** Type guard to check if error is already a ServiceError */
  private isServiceError(err: unknown): err is ServiceError {
    return (
      typeof err === 'object' &&
      err !== null &&
      ('base' in err ||
       'number' in err || 'exp' in err || 'cvc' in err || 'zip' in err ||
       'account_number' in err || 'routing_number' in err ||
       'account_holder_name' in err || 'account_holder_type' in err)
    );
  }

  /**
   * Check if an error is a transient network failure that should be retried.
   * Returns true for network timeouts, DNS failures, connection errors.
   * Returns false for Stripe validation errors (card_error) which should not be retried.
   */
  private isRetryableNetworkError(err: unknown): boolean {
    if (this.isServiceError(err)) {
      // ServiceErrors are already-parsed Stripe validation errors - don't retry
      return false;
    }

    if (err instanceof Error) {
      const message = err.message.toLowerCase();
      return (
        message.includes('network') ||
        message.includes('timeout') ||
        message.includes('fetch') ||
        message.includes('failed to fetch') ||
        message.includes('dns')
      );
    }

    return false;
  }

  /** Type guard to distinguish Payment Element from card elements */
  private isPaymentElement(element: any): element is StripePaymentElement {
    // Payment Element doesn't have 'mount' method - it's already handled by elements.create()
    // Card elements have mount/unmount/on/off methods
    return !('mount' in element) || element.elementType === 'payment';
  }

  /** Create payment method using Payment Element API */
  private createWithPaymentElement(billingDetails?: CreditCardForm) {
    return this.stripe!.createPaymentMethod({
      elements: this.elements!,
      params: {
        billing_details: this.formatBillingDetails(billingDetails),
      },
    });
  }

  /** Create payment method using legacy card element API */
  private createWithCardElement(
    cardElement: StripeCardNumberElement,
    billingDetails?: CreditCardForm,
  ) {
    return this.stripe!.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: this.formatBillingDetails(billingDetails),
    });
  }

  /** Format billing details for Stripe API */
  private formatBillingDetails(billingDetails?: CreditCardForm) {
    return {
      name: billingDetails?.name,
      address: {
        line1: billingDetails?.address_line1,
        line2: billingDetails?.address_line2,
        city: billingDetails?.address_city,
        state: billingDetails?.address_state,
        postal_code: billingDetails?.address_zip,
        country: 'US',
      },
    };
  }

  /** Map Stripe PaymentMethod to our result interface */
  private mapPaymentMethod(pm: PaymentMethod): IPaymentMethodResult {
    switch (pm.type) {
      case 'card': {
        // Stripe.js v4 PaymentMethodCard type doesn't expose `id`, but the runtime payload does.
        const card = pm.card as (typeof pm.card & { id?: string; }) | undefined;
        return {
          paymentMethodId: pm.id,
          type: 'card',
          cardDetails: {
            brand: pm.card?.brand ?? '',
            last4: pm.card?.last4 ?? '',
            country: pm.card?.country ?? '',
            cardId: card?.id ?? '',
          },
        };
      }

      case 'us_bank_account':
        return {
          paymentMethodId: pm.id,
          type: 'us_bank_account',
          bankDetails: {
            bankName: pm.us_bank_account?.bank_name ?? '',
            last4: pm.us_bank_account?.last4 ?? '',
            accountHolderType: pm.us_bank_account?.account_holder_type,
          },
        };

      case 'card_present':
      case 'apple_pay':
      case 'google_pay':
      case 'link':
      default:
        // Wallets and future payment types don't expose detailed info
        return {
          paymentMethodId: pm.id,
          type: pm.type,
        };
    }
  }

  /** Parse payment errors (supports more than just card errors) */
  parsePaymentError(error: StripeError): ServiceError {
    if (error.type === 'card_error') {
      return this.parseCardError(error);
    }

    if (error.type === 'validation_error') {
      // Payment Element validation errors
      return { base: [this.formatStripeErrorMessage(error, VALIDATION_ERROR_FALLBACK)] };
    }

    if (error.type === 'invalid_request_error') {
      // Not `{ error }`: that would log the card's brand, last4 and expiry.
      this.logger.error('Stripe invalid request error', {
        code: error.code,
        declineCode: error.decline_code,
        stripeMessage: error.message,
        setupIntentId: error.setup_intent?.id
      });
      return { base: [this.formatStripeErrorMessage(error, INVALID_REQUEST_FALLBACK)] };
    }

    // Generic error
    return { base: [this.formatStripeErrorMessage(error, GENERIC_CARD_ERROR)] };
  }

  /** Stripe's message plus its code(s), so support can match the error without Stripe access. */
  private formatStripeErrorMessage(error: StripeError, fallback: string): string {
    const message = error.message?.trim();
    if (!message) {
      return fallback;
    }

    const parts: string[] = [];
    if (error.code) {
      parts.push(`Code: ${error.code}`);
    }
    if (error.decline_code && !HIDDEN_DECLINE_CODES.has(error.decline_code)) {
      parts.push(`Decline code: ${error.decline_code}`);
    }

    return parts.length > 0 ? `${message} (${parts.join(', ')})` : message;
  }

  /**
   * Wraps Stripe's `Elements.getElement` / `Elements.create` for one element type.
   * Styling is handled by the Appearance API configured at Elements creation.
   */
  private getOrCreateElement(
    type: IStripeFormElementSpec['type'],
  ): StripeFormElement {
    const { elements } = this;
    if (!elements) {
      throw new Error('getOrCreateElement called before Stripe finished loading.');
    }
    const elementStyle = {
      base: {
        backgroundColor: '#FFFFFF',
        color: colorNeutralDefault,
        fontSize: fontSize04,
        fontFamily: fontFamilyFallback,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '::placeholder': {
          color: colorNeutral15,
        },
      },
      invalid: {
        color: colorMessageErrorBase,
        iconColor: colorMessageErrorBase,
      },
    };

    switch (type) {
      case 'cardNumber':
        return elements.getElement('cardNumber') ?? elements.create('cardNumber', {
          placeholder: 'Card number',
          style: elementStyle,
        });
      case 'cardExpiry':
        return elements.getElement('cardExpiry') ?? elements.create('cardExpiry', {
          placeholder: 'MM/YY',
          style: elementStyle,
        });
      case 'cardCvc':
        return elements.getElement('cardCvc') ?? elements.create('cardCvc', {
          placeholder: 'CVC',
          style: elementStyle,
        });
      default:
        throw new Error(`Unknown Stripe element type: ${String(type)}`);
    }
  }

  /**
   * Creates Stripe Appearance API config using Iris design tokens.
   * Note: Individual card elements (cardNumber, cardExpiry, cardCvc) don't fully support
   * the Appearance API - they use the `style` option in getOrCreateElement instead.
   */
  private createAppearanceConfig() {
    return {
      variables: {
        colorPrimary: colorActionActive,
        colorBackground: '#FFFFFF',
        colorText: colorNeutralDefault,
        colorDanger: colorMessageErrorBase,
        fontFamily: fontFamilyFallback,
        fontSizeBase: fontSize04,
        fontWeightNormal: fontWeightMedium,
        borderRadius: borderRadius04,
      },
    };
  }

  /** Updates per-element error/complete state and recomputes `cardErrorMessage`. */
  private onElementChange(event: StripeElementChangeEvent, index: number): void {
    this.stripeComplete.update((prev) => ({ ...prev, [index]: event.complete }));

    if (event.error) {
      const { message } = event.error;
      this.stripeErrors.update((prev) => ({ ...prev, [index]: message }));
      this.cardErrorMessage.set({ title: message });
      return;
    }

    this.stripeErrors.update((prev) => ({ ...prev, [index]: null }));

    const errors = this.stripeErrors();
    const firstError = Object.keys(errors)
      .map(Number)
      .sort((a, b) => a - b)
      .reduce<string | null>((found, key) => found ?? errors[key], null);

    this.cardErrorMessage.set(firstError ? { title: firstError } : null);
  }
}
